import { Grid, InputAdornment } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import MuiToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { AutoComplete, Icons, Select } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { IFieldInspectorList, IViewInspectorDocument } from '../../models/models';
import { isJestTest } from '../../test-helpers/isJestTest';
import { mockSPList } from '../../test-helpers/mockData/assignInspector';
import { mockFIList } from '../../test-helpers/mockData/viewInspectors';
import { useNotification } from '../../utilities';
import useStyles from './AddInspectors.styles';

const ToggleButton = styled(MuiToggleButton)({
  '&.Mui-selected:not(.Mui-disabled), &.Mui-selected:hover:not(.Mui-disabled)': {
    backgroundColor: 'rgba(42, 171, 63, 1)',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    color: '#121212',
  },
});

interface Props {
  handleSearch: (
    event: React.KeyboardEvent<HTMLDivElement>,
    value: {
      pkInspector: number;
      firstName: string;
      lastName: string;
      clerkId: number;
      clerkName: string;
    },
  ) => void;
  fiListToAssignInspector: IViewInspectorDocument[];
  isDECView: boolean;
}

const AddInspector: React.FC<Props> = ({
  handleSearch,
  fiListToAssignInspector,
  isDECView,
}: Props) => {
  const classes = useStyles();
  const { primaryContext } = useMst();
  const { errorNotification } = useNotification();
  const [inspectorType, setInspectorType] = useState<string>('Nutrien');
  const [spId, setSpId] = useState<number>(1);
  const fieldInspectorList = isDECView
    ? primaryContext.dataEntryClerksList
    : primaryContext.fieldInspectorList;

  const handleToggleChange = (
    _event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      if (inspType === 'Nutrien') {
        setSpId(1);
      }
      setInspectorType(inspType);
    }
  };

  const checkIsThereInSelected = (inspectorId: number, clerkId: number) => {
    return (
      fiListToAssignInspector.findIndex(inspector => {
        if (inspector.fieldInspectorId) {
          return inspector.fieldInspectorId === inspectorId;
        } else {
          if (inspector.clerkId) {
            return inspector.clerkId === clerkId;
          }
        }
      }) === -1
    );
  };

  useEffect(() => {
    let isMounted = true;
    const getMasterData = async () => {
      try {
        if (isMounted) {
          await Promise.all([
            primaryContext.getFIListFromMobx(),
            primaryContext.getDataEntryClerksFromMobx(),
            primaryContext.getSPListFromMobx(),
          ]);
        }
      } catch (e) {
        if (isMounted) {
          errorNotification('data is not loaded');
        }
      }
    };
    getMasterData();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Grid item xs={12} alignItems="center">
      <ToggleButtonGroup
        color="#121212"
        value={inspectorType}
        exclusive
        onChange={handleToggleChange}
      >
        <ToggleButton value="Nutrien" data-testid="nutrientogglebutton">
          Nutrien
        </ToggleButton>
        <ToggleButton value="ServiceProvider" data-testid="sptogglebutton">
          Service Provider
        </ToggleButton>
      </ToggleButtonGroup>

      {inspectorType === 'ServiceProvider' && (
        <Grid className={classes.spSelectGrid} item xs={12}>
          <Select
            className={classes.spSelect}
            label="Service Provider"
            list={
              isJestTest()
                ? mockSPList
                : primaryContext.serviceProviderList.filter(e => e.pkServiceProvider !== 1)
            }
            getOptionLabel={list => `${list.organizationName}`}
            getOptionValue={list => list.pkServiceProvider}
            onChange={event => {
              setSpId(event.target.value);
            }}
            defaultValue={spId}
            noMinHeight
            data-testid="splistoptions"
          />
        </Grid>
      )}

      <Grid item className={classes.autoCompleteGrid}>
        <AutoComplete
          autoSelect={false}
          key="field-inspector"
          noMinHeight
          size="small"
          label={isDECView ? 'Data Entry Clerk' : 'Field Inspector'}
          closeIcon=""
          startAdornment={
            <InputAdornment position="start">
              <Icons.SearchFeather color="rgba(149, 149, 149, 1)" height={20} width={20} />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              <Icons.PlusCircleFeather
                className={classes.plusIcon}
                color="primary"
                strokeWidth="1.5px"
              />
            </InputAdornment>
          }
          placeholder={isDECView ? 'Search Data Entry Clerk' : 'Search Inspector'}
          onChange={(event, newValue) => {
            if (newValue) handleSearch(event, newValue);
          }}
          list={
            isJestTest()
              ? mockFIList
              : fieldInspectorList
                  .filter(
                    (value: IFieldInspectorList) =>
                      value.fkServiceProvider === spId &&
                      value.isActive &&
                      checkIsThereInSelected(value.pkInspector as number, value.clerkId as number),
                  )
                  .sort((a: IFieldInspectorList, b: IFieldInspectorList) =>
                    a.firstName.concat(a.lastName)?.toLowerCase() <
                    b.firstName.concat(b.lastName)?.toLowerCase()
                      ? -1
                      : 1,
                  )
          }
          value=""
          getOptionLabel={option =>
            option ? `${`${option.firstName} ${option.lastName}`}` : option
          }
          data-testid="fieldInspectorBox"
        />
      </Grid>
    </Grid>
  );
};

export default observer(AddInspector);
